import {
  CarePractitionerFragment,
  useCarePractitionersByDoctorIdsQuery,
} from "@components/Booking/__generated__/care-practitioners-by-doctor-ids.type";
import {LocSlotPair} from "@components/Locations/usePairLocsWithSlots";
import {ApptSlot} from "@services/monolith/availability";
import {compact, uniq} from "lodash";
import {useMemo} from "react";

/**
 * Groups a single list of slots by providers.
 */
export const useGroupSlotsByDoctors = <T extends {id: string; specialtyIds: string[]}>({
  slots,
  location,
  skip,
}: {
  slots: ApptSlot[];
  skip?: boolean;
  location: T;
}): {
  loading: boolean;
  data: (LocSlotPair<T> & {doctor: CarePractitionerFragment})[];
} => {
  /**
   * Get all of the unique doctor ids from slot groups
   */
  const allDoctorIds = useMemo(() => {
    if (!slots) return [];
    return uniq(slots.map(slot => slot.doctorId));
  }, [slots]);

  /**
   * Fetch all of the doctors
   */
  const doctorsQuery = useCarePractitionersByDoctorIdsQuery({
    variables: {
      doctorIds: allDoctorIds,
    },
    skip: skip || allDoctorIds.length < 1,
  });

  const data = useMemo(() => {
    const doctors = compact(doctorsQuery.data?.carePractitionersByDoctorIds) || [];
    return doctors.map(doctor => {
      const slotsForThisDoctor = slots?.filter(slot => slot.doctorId === doctor.id) || [];

      return {
        doctor,
        location,
        soonestSlot: slotsForThisDoctor[0],
        slots: slotsForThisDoctor,
      };
    });
  }, [doctorsQuery.data?.carePractitionersByDoctorIds, slots, location]);

  return {
    data,
    loading: doctorsQuery.loading,
  };
};
